/**
 *
 * LandingPage
 *
 */
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../components/Spinner/Spinner';
import { LatLng } from '../../firebase/firebase.types';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { PlacePrediction } from '../../hooks/usePlacesAutocomplete';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectCurrentTrip, selectTripCreationStatus } from '../../redux/trip/trip.selectors';
import { createTrip, resetTripCreationStatus } from '../../redux/trip/trip.slice';
import { TripCreationStatus } from '../../redux/trip/trip.types';
import { selectCurrentUser } from '../../redux/user/user.selectors';
import { getDetails } from '../../utils/places-api.utils';
import AnimatedPlaceholderInput from './HomePlaceholder';
import './Home.scss';

function devLog(message: string, data?: unknown) {
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.log(message, data);
  }
}

function hasGeometry(
  result: google.maps.places.PlaceResult | string
): result is google.maps.places.PlaceResult {
  return (result as google.maps.places.PlaceResult).geometry !== undefined;
}

type Place = {
  description: string;
  location: LatLng;
};

function Home() {
  const dispatch = useAppDispatch();
  const isMobile = useMobileMediaQuery();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const tripCreationStatus = useAppSelector(selectTripCreationStatus);
  const currentTrip = useAppSelector(selectCurrentTrip);
  const [place, setPlace] = useState<Place | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (tripCreationStatus === TripCreationStatus.CREATED) {
      dispatch(resetTripCreationStatus());
      if (currentTrip) {
        navigate(`/trips/${currentTrip.id}`);
      }
    }
  }, [currentTrip, dispatch, navigate, tripCreationStatus]);

  const handleSuggestionSelect = (
    suggestion: PlacePrediction,
    sessionToken: string,
    usedNewApi: boolean
  ) => {
    const { place_id, description } = suggestion;

    // Only use session token if New API was used for autocomplete
    const sessionTokenToUse = usedNewApi ? sessionToken : undefined;

    getDetails({
      placeId: place_id,
      fields: ['geometry.location'],
      sessionToken: sessionTokenToUse,
    })
      .then((details) => {
        if (hasGeometry(details) && details?.geometry?.location) {
          const latitude = details?.geometry?.location?.lat();
          const longitude = details?.geometry?.location?.lng();
          setPlace({
            description,
            location: { latitude, longitude },
          });
        }
      })
      .catch((error) => {
        devLog('Error ', error);
      });
  };

  const handleStartPlanning = () => {
    if (place) {
      if (currentUser) {
        dispatch(
          createTrip({ place: place.description, location: place.location, user: currentUser })
        );
      }
      setHasError(false);
    } else {
      setHasError(true);
    }
  };

  const navigateToSignInSignUp = () => navigate('/login');

  return tripCreationStatus === TripCreationStatus.CREATING ? (
    <Spinner />
  ) : (
    <>
      <Helmet>
        <title>Wander | Home</title>
      </Helmet>
      <div className='home-container'>
        <div className='logo-container'>
          <img
            className='logo-img'
            src='/svgs/logo_2.png'
            alt='wander-logo'
          />
        </div>
        {currentUser ? (
          <>
            <Fade
              in={true}
              timeout={2100}
            >
              <h2 className='home-header-subtext'>A planner for your next</h2>
            </Fade>
            <div className='home-form-container'>
              <div className='home-form-label'>Adventure to</div>
              <AnimatedPlaceholderInput
                isMobile={isMobile}
                onSuggestionSelect={handleSuggestionSelect}
                setHasError={setHasError}
                hasError={hasError}
              />
            </div>
            {hasError && <div className='home-error-msg'>Select a Location.</div>}
            <Button
              className='button start-planning-btn'
              variant='outlined'
              onClick={handleStartPlanning}
            >
              Start Planning
            </Button>
          </>
        ) : (
          <>
            <Fade
              in={true}
              timeout={2100}
            >
              <h1 className='home-header'>A planner for your next adventure</h1>
            </Fade>
            <Button
              className='button start-planning-btn'
              variant='outlined'
              onClick={navigateToSignInSignUp}
            >
              Get Started
            </Button>
          </>
        )}
        <h1 className='home-footer'>Made with ❤️ from a team of passionate travelers</h1>
      </div>
    </>
  );
}

Home.displayName = 'Home';
export default Home;
